import { get, post } from './api'

export type PushState =
  | 'unsupported'
  | 'not-configured'
  /** No service worker is registered, so push cannot be set up (dev builds). */
  | 'no-service-worker'
  | 'denied'
  | 'prompt'
  | 'subscribed'

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalised)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export async function currentPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'

  // `serviceWorker.ready` never settles when no worker is registered, which is
  // the case in `vite dev` — the service worker is only built for production.
  // Without a bound wait, every caller of this hangs and the UI that depends on
  // it silently never renders.
  const registration = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
  ])
  if (!registration) return 'no-service-worker'

  const existing = await registration.pushManager.getSubscription()
  return existing ? 'subscribed' : 'prompt'
}

/**
 * Ask for permission, subscribe with the server's VAPID key, and register the
 * subscription. Returns the resulting state so the caller can explain what
 * happened rather than silently failing.
 */
export async function enablePush(): Promise<{
  state: PushState
  message?: string
  /** The underlying browser error, for the console — never shown to a worker. */
  detail?: string
}> {
  if (!pushSupported()) {
    return {
      state: 'unsupported',
      message: 'This browser cannot receive reminders. On iPhone, add the app to your Home Screen first.',
    }
  }

  const { publicKey, configured } = await get<{ publicKey?: string; configured: boolean }>(
    '/api/push/key',
  ).catch(() => ({ publicKey: undefined, configured: false }))

  if (!configured || !publicKey) {
    return { state: 'not-configured', message: 'Push is not set up on this deployment yet.' }
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return {
      state: 'denied',
      message: 'Reminders are blocked. Turn on notifications for this app in your browser settings.',
    }
  }

  const registration = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
  ])
  if (!registration) {
    return {
      state: 'no-service-worker',
      message: 'Reminders need the installed app. This looks like a development build.',
    }
  }

  let subscription = await registration.pushManager.getSubscription()

  // A key rotation leaves a stale subscription that will never decrypt.
  if (subscription) {
    const existingKey = subscription.options.applicationServerKey
    const wanted = urlBase64ToUint8Array(publicKey)
    if (!existingKey || !sameKey(new Uint8Array(existingKey), wanted)) {
      await subscription.unsubscribe()
      subscription = null
    }
  }

  // Subscribing talks to the browser vendor's push service, so it can fail for
  // reasons that have nothing to do with this app — no network, a restricted
  // device profile, or a browser build with push disabled. The raw error
  // ("Registration failed - permission denied") would only confuse a worker who
  // has just granted permission, so translate it.
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      })
    } catch (err) {
      return {
        state: 'prompt',
        message: 'Your phone would not register for reminders. Check you are online, then try again.',
        detail: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
      }
    }
  }

  const json = subscription.toJSON() as {
    endpoint?: string
    keys?: { p256dh?: string; auth?: string }
  }
  await post('/api/push/subscribe', { endpoint: json.endpoint, keys: json.keys })

  return { state: 'subscribed' }
}

function sameKey(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/**
 * Re-align the browser's subscription with the server, silently.
 *
 * The two can drift: the VAPID keys rotate (the account migration did this) or
 * the server's registration row is deleted, while the browser still holds a
 * subscription. The UI then says reminders are on while the server has nowhere
 * to send them — "no device registered" on a phone that thinks it is. Both
 * repairs need no user gesture, since permission is already granted: a rotated
 * key means resubscribe under the new one; a missing row is healed by
 * re-registering, which is an idempotent upsert keyed on the endpoint.
 */
export async function reconcilePush(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return

  const registration = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
  ])
  if (!registration) return
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  const { publicKey, configured } = await get<{ publicKey?: string; configured: boolean }>(
    '/api/push/key',
  ).catch(() => ({ publicKey: undefined, configured: false }))
  if (!configured || !publicKey) return

  const wanted = urlBase64ToUint8Array(publicKey)
  const existingKey = subscription.options.applicationServerKey

  if (!existingKey || !sameKey(new Uint8Array(existingKey), wanted)) {
    await subscription.unsubscribe().catch(() => undefined)
    try {
      const fresh = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: wanted as BufferSource,
      })
      const json = fresh.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }
      await post('/api/push/subscribe', { endpoint: json.endpoint, keys: json.keys })
    } catch (err) {
      console.warn('push reconcile: resubscribe failed:', err)
    }
    return
  }

  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }
  await post('/api/push/subscribe', { endpoint: json.endpoint, keys: json.keys }).catch(() => undefined)
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return
  await post('/api/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => undefined)
  await subscription.unsubscribe()
}

/**
 * A local notification, shown by the page's own service worker. Used for the
 * "you have moved sites" prompt, which depends on GPS the server cannot see.
 */
export async function showLocalNotification(
  title: string, options: NotificationOptions & { data?: Record<string, unknown> },
): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return
  const registration = await navigator.serviceWorker.ready
  await registration.showNotification(title, {
    badge: '/icons/badge-96.png',
    icon: '/icons/icon-192.png',
    ...options,
  })
}
